import { useEffect, useMemo, useState } from "react";
import OverlayShell from "./OverlayShell";
import { ActionButton } from "./styles";
import { IMAGES } from "./images";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ClaimGroupModal = ({
  groups = [],
  initialValues,
  loadingGroups,
  loadingGroupsError,
  submitting,
  submitError,
  onSubmit,
  onTrackButtonClick
}) => {
  const [step, setStep] = useState("form");
  const [formValues, setFormValues] = useState({
    guestFirstName: "",
    guestLastName: "",
    email: "",
    guestGroupId: ""
  });
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);

  useEffect(() => {
    setFormValues({
      guestFirstName: initialValues?.guestFirstName || "",
      guestLastName: initialValues?.guestLastName || "",
      email: initialValues?.email || "",
      guestGroupId: ""
    });
  }, [initialValues]);

  const normalizedGroups = useMemo(() => {
    return (groups || [])
      .map((group) => {
        if (typeof group === "string") {
          return { id: group, name: group };
        }

        return {
          id:
            group?.id ||
            group?.groupId ||
            group?.guestGroupId ||
            group?.value ||
            "",
          name: group?.name || group?.customFields?.hotelName?.value || ""
        };
      })
      .filter((group) => group.id && group.name);
  }, [groups]);

  const errors = useMemo(() => {
    const nextErrors = {};

    if (!formValues.guestFirstName.trim()) {
      nextErrors.guestFirstName = "First name is required.";
    }
    if (!formValues.guestLastName.trim()) {
      nextErrors.guestLastName = "Last name is required.";
    }
    if (!formValues.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(formValues.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formValues.guestGroupId) {
      nextErrors.guestGroupId = "Please select your group.";
    }

    return nextErrors;
  }, [formValues]);

  const isValid = Object.keys(errors).length === 0;
  const selectedGroup = normalizedGroups.find(
    (group) => group.id === formValues.guestGroupId
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContinue = () => {
    setHasSubmittedForm(true);
    if (!isValid) {
      return;
    }

    onTrackButtonClick?.("continue_to_confirmation", "claim_group_modal");
    setStep("confirm");
  };

  const handleConfirmSubmit = () => {
    onTrackButtonClick?.("confirm_claim_submit", "claim_group_modal");
    onSubmit({
      guestFirstName: formValues.guestFirstName.trim(),
      guestLastName: formValues.guestLastName.trim(),
      email: formValues.email.trim(),
      guestGroupId: formValues.guestGroupId
    });
  };

  return (
    <OverlayShell title="Your details" image={IMAGES.hotel}>
      {step === "form" && (
        <div className="section claim-form">
          <p>
            Please complete your details to continue. This is needed only once.
          </p>
          <label htmlFor="guestFirstName">First name</label>
          <input
            id="guestFirstName"
            name="guestFirstName"
            value={formValues.guestFirstName}
            onChange={handleInputChange}
            disabled={submitting}
            autoComplete="given-name"
            aria-invalid={hasSubmittedForm && Boolean(errors.guestFirstName)}
          />
          {hasSubmittedForm && errors.guestFirstName && (
            <p className="error-message">{errors.guestFirstName}</p>
          )}

          <label htmlFor="guestLastName">Last name</label>
          <input
            id="guestLastName"
            name="guestLastName"
            value={formValues.guestLastName}
            onChange={handleInputChange}
            disabled={submitting}
            autoComplete="family-name"
            aria-invalid={hasSubmittedForm && Boolean(errors.guestLastName)}
          />
          {hasSubmittedForm && errors.guestLastName && (
            <p className="error-message">{errors.guestLastName}</p>
          )}

          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            value={formValues.email}
            onChange={handleInputChange}
            disabled={submitting}
            type="email"
            autoComplete="email"
            aria-invalid={hasSubmittedForm && Boolean(errors.email)}
          />
          {hasSubmittedForm && errors.email && (
            <p className="error-message">{errors.email}</p>
          )}

          <label htmlFor="guestGroupId">Group</label>
          <select
            id="guestGroupId"
            name="guestGroupId"
            value={formValues.guestGroupId}
            onChange={handleInputChange}
            disabled={loadingGroups || submitting}
            aria-invalid={hasSubmittedForm && Boolean(errors.guestGroupId)}
          >
            <option value="">Select your group</option>
            {normalizedGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
          {hasSubmittedForm && errors.guestGroupId && (
            <p className="error-message">{errors.guestGroupId}</p>
          )}
          {loadingGroups && <p>Loading groups...</p>}
          {loadingGroupsError && (
            <p className="error-message">{loadingGroupsError}</p>
          )}

          <ActionButton
            type="button"
            disabled={submitting}
            onClick={handleContinue}
          >
            Continue
          </ActionButton>
        </div>
      )}

      {step === "confirm" && (
        <div className="section claim-form">
          <p className="bold">Please confirm your details</p>
          <div className="confirm-item">
            <p className="bold">First name</p>
            <p>{formValues.guestFirstName}</p>
          </div>
          <div className="confirm-item">
            <p className="bold">Last name</p>
            <p>{formValues.guestLastName}</p>
          </div>
          <div className="confirm-item">
            <p className="bold">Email</p>
            <p>{formValues.email}</p>
          </div>
          <div className="confirm-item">
            <p className="bold">Group</p>
            <p>{selectedGroup?.name || formValues.guestGroupId}</p>
          </div>
          {submitError && <p className="error-message">{submitError}</p>}
          <ActionButton
            type="button"
            $variant="ghost"
            disabled={submitting}
            onClick={() => {
              onTrackButtonClick?.("edit_claim_data", "claim_group_modal");
              setStep("form");
            }}
          >
            Edit
          </ActionButton>
          <ActionButton
            type="button"
            disabled={submitting}
            onClick={handleConfirmSubmit}
          >
            {submitting ? "Submitting..." : "Confirm & submit"}
          </ActionButton>
        </div>
      )}
    </OverlayShell>
  );
};

export default ClaimGroupModal;
